/**
 * TimeClockManagerPage - Dashboard Manager pour le pointage
 * Vue temps réel de l'équipe, alertes, rapports
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Users, UserCheck, UserX, Coffee, AlertTriangle,
  TrendingUp, Calendar, Download, Filter, RefreshCw,
  ChevronRight, Eye, FileText, Timer, Activity,
  CheckCircle, XCircle, AlertCircle, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ══════════════════════════════════════════════════════════════════════════════

// Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-green-100', text: 'text-green-700', icon: Activity, label: 'Actif' },
    idle: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Timer, label: 'Inactif' },
    absent: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Coffee, label: 'Absent' },
    completed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Terminé' },
    not_clocked: { bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle, label: 'Non pointé' },
    late: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'En retard' },
  };
  
  const { bg, text, icon: Icon, label } = config[status] || config.not_clocked;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon size={12} />
      {label}
    </span>
  );
};

// Employee Card
const EmployeeCard = ({ member, onViewDetails }) => {
  const clockInTime = member.clock_in 
    ? new Date(member.clock_in).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : null;
  const clockOutTime = member.clock_out
    ? new Date(member.clock_out).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border rounded-xl p-4 hover:shadow-md transition-shadow ${
        member.is_late ? 'border-l-4 border-l-red-500' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
            member.status === 'active' ? 'bg-green-500' :
            member.status === 'idle' ? 'bg-yellow-500' :
            member.status === 'absent' ? 'bg-orange-500' :
            member.status === 'completed' ? 'bg-blue-500' :
            'bg-gray-400'
          }`}>
            {member.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{member.name || 'Employé'}</h3>
            <p className="text-xs text-gray-500">{member.role || 'Membre'}</p>
          </div>
        </div>
        <StatusBadge status={member.is_late && member.status !== 'not_clocked' ? 'late' : member.status} />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Entrée</p>
          <p className="font-semibold text-sm">{clockInTime || '—'}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Sortie</p>
          <p className="font-semibold text-sm">{clockOutTime || '—'}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-500">Heures</p>
          <p className="font-semibold text-sm">{member.total_hours?.toFixed(1) || '—'}h</p>
        </div>
      </div>

      {member.is_late && member.late_minutes > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <AlertTriangle size={14} />
          Retard de {member.late_minutes} min
        </div>
      )}

      <button
        onClick={() => onViewDetails(member)}
        className="mt-3 w-full text-center text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center justify-center gap-1"
      >
        Voir détails <ChevronRight size={16} />
      </button>
    </motion.div>
  );
};

// Stats Card
const StatsCard = ({ icon: Icon, label, value, color = 'gray', subvalue }) => (
  <Card className={`border-l-4 border-l-${color}-500`}>
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`p-3 bg-${color}-100 rounded-xl`}>
        <Icon size={24} className={`text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {subvalue && <p className="text-xs text-gray-400 mt-0.5">{subvalue}</p>}
      </div>
    </CardContent>
  </Card>
);

// Employee Detail Modal
const EmployeeDetailModal = ({ employee, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const res = await axios.get(`${API}/timeclock/activity/report/${employee.user_id}?date=${today}`);
        setReport(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [employee.user_id]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-lg">
              {employee.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-xl font-bold">{employee.name}</h2>
              <p className="text-sm text-gray-500">{employee.role}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <XCircle size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Status actuel */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <span className="text-sm text-gray-600">Statut actuel</span>
            <StatusBadge status={employee.status} />
          </div>

          {/* Horaires */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-xs text-green-600 font-medium">Pointage entrée</p>
              <p className="text-xl font-bold text-green-700">
                {employee.clock_in 
                  ? new Date(employee.clock_in).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-xs text-blue-600 font-medium">Pointage sortie</p>
              <p className="text-xl font-bold text-blue-700">
                {employee.clock_out 
                  ? new Date(employee.clock_out).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
            </div>
          </div>

          {/* Rapport d'activité */}
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="animate-spin text-gray-400" size={24} />
            </div>
          ) : report?.data !== null ? (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700">Activité aujourd'hui</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{report?.total_hours?.toFixed(1) || 0}h</p>
                  <p className="text-xs text-gray-500">Heures travaillées</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{report?.activity_rate || 0}%</p>
                  <p className="text-xs text-gray-500">Taux d'activité</p>
                </div>
              </div>

              {report?.idle_periods?.length > 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm font-medium text-yellow-700 mb-2">Périodes d'inactivité</p>
                  {report.idle_periods.slice(0, 3).map((period, i) => (
                    <div key={i} className="text-xs text-yellow-600">
                      {new Date(period.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} 
                      {' → '}
                      {new Date(period.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  ))}
                </div>
              )}

              {report?.absences?.length > 0 && (
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm font-medium text-orange-700 mb-2">Absences déclarées</p>
                  {report.absences.map((absence, i) => (
                    <div key={i} className="text-xs text-orange-600 flex justify-between">
                      <span>{absence.reason}</span>
                      <span>{absence.actual_duration_minutes || absence.expected_duration_minutes} min</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">Pas de données d'activité</p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════

export default function TimeClockManagerPage() {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // TODO: Get real team ID from context
  const teamId = 'current-team-id';

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeclock/manager/team/${teamId}/today`);
      setTeamData(res.data);
    } catch (error) {
      // Fallback avec données de démo si pas d'équipe
      setTeamData({
        date: new Date().toISOString().split('T')[0],
        team_id: teamId,
        team_name: 'Mon Équipe',
        members: [
          { user_id: '1', name: 'Marie Dupont', role: 'Développeur', status: 'active', clock_in: new Date().toISOString(), clock_out: null, is_late: false, late_minutes: 0, total_hours: 4.5 },
          { user_id: '2', name: 'Jean Martin', role: 'Designer', status: 'idle', clock_in: new Date(Date.now() - 3600000 * 5).toISOString(), clock_out: null, is_late: true, late_minutes: 15, total_hours: 5.2 },
          { user_id: '3', name: 'Sophie Bernard', role: 'Chef de projet', status: 'absent', clock_in: new Date(Date.now() - 3600000 * 3).toISOString(), clock_out: null, is_late: false, late_minutes: 0, total_hours: 3.0 },
          { user_id: '4', name: 'Pierre Durand', role: 'Commercial', status: 'completed', clock_in: new Date(Date.now() - 3600000 * 8).toISOString(), clock_out: new Date(Date.now() - 3600000).toISOString(), is_late: false, late_minutes: 0, total_hours: 7.0 },
          { user_id: '5', name: 'Julie Moreau', role: 'RH', status: 'not_clocked', clock_in: null, clock_out: null, is_late: false, late_minutes: 0, total_hours: 0 },
        ],
        summary: { total: 5, active: 1, idle: 1, absent: 1, completed: 1, not_clocked: 1 }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
    // Refresh toutes les 2 minutes
    const interval = setInterval(fetchTeamData, 120000);
    return () => clearInterval(interval);
  }, []);

  const filteredMembers = teamData?.members?.filter(m => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'late') return m.is_late;
    return m.status === filterStatus;
  }) || [];

  if (loading && !teamData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  const summary = teamData?.summary || {};

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="timeclock-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="text-orange-500" />
            Pointage Manager
          </h1>
          <p className="text-gray-500 mt-1">
            {teamData?.team_name} • {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchTeamData}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button variant="outline" size="sm">
            <Download size={16} className="mr-2" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatsCard icon={Users} label="Total" value={summary.total || 0} color="gray" />
        <StatsCard icon={Activity} label="Actifs" value={summary.active || 0} color="green" />
        <StatsCard icon={Timer} label="Inactifs" value={summary.idle || 0} color="yellow" />
        <StatsCard icon={Coffee} label="Absents" value={summary.absent || 0} color="orange" />
        <StatsCard icon={XCircle} label="Non pointés" value={summary.not_clocked || 0} color="red" />
      </div>

      {/* Alertes */}
      {teamData?.members?.some(m => m.is_late) && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="text-red-500" size={24} />
            <div>
              <p className="font-semibold text-red-700">
                {teamData.members.filter(m => m.is_late).length} retard(s) aujourd'hui
              </p>
              <p className="text-sm text-red-600">
                {teamData.members.filter(m => m.is_late).map(m => m.name).join(', ')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 mr-2">Filtrer :</span>
        {[
          { id: 'all', label: 'Tous' },
          { id: 'active', label: 'Actifs' },
          { id: 'idle', label: 'Inactifs' },
          { id: 'absent', label: 'Absents' },
          { id: 'completed', label: 'Terminés' },
          { id: 'not_clocked', label: 'Non pointés' },
          { id: 'late', label: 'En retard' },
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => setFilterStatus(filter.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              filterStatus === filter.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Liste des employés */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMembers.map((member) => (
          <EmployeeCard
            key={member.user_id}
            member={member}
            onViewDetails={setSelectedEmployee}
          />
        ))}
        {filteredMembers.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p>Aucun employé trouvé avec ce filtre</p>
          </div>
        )}
      </div>

      {/* Modal détail */}
      <AnimatePresence>
        {selectedEmployee && (
          <EmployeeDetailModal
            employee={selectedEmployee}
            onClose={() => setSelectedEmployee(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
